import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Upload, FileText, TestTube, Radiation, Pill, Hospital,
  Microscope, Scissors, FileQuestion, Loader2, Download, Trash2,
  AlertCircle, CheckCircle2, ChevronRight, X, Clock,
} from "lucide-react";

/**
 * Universal Medical Documents page — patient uploads any medical report
 * (lab, radiology, prescription, discharge, pathology, ops note). AI vision engine
 * classifies + extracts structured data + generates a plain-English summary.
 */

const DOC_TYPE_META = {
  lab_report:         { label: "Lab Report",         icon: TestTube,      accent: "#4ADE80" },
  radiology_report:   { label: "Radiology Report",   icon: Radiation,     accent: "#60A5FA" },
  prescription:       { label: "Prescription",       icon: Pill,          accent: "#A78BFA" },
  discharge_summary:  { label: "Discharge Summary",  icon: Hospital,      accent: "#FBBF24" },
  pathology_report:   { label: "Pathology Report",   icon: Microscope,    accent: "#F472B6" },
  operative_note:     { label: "Operative Note",     icon: Scissors,      accent: "#F87171" },
  other:              { label: "Document",           icon: FileQuestion,  accent: "#A3ACA7" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents");
      setDocs(data.documents || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 3s while any doc is still pending or processing.
  useEffect(() => {
    const hasInFlight = docs.some(
      (d) => d.ai_status === "pending" || d.ai_status === "processing"
    );
    if (!hasInFlight) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/documents");
        setDocs(data.documents || []);
      } catch {
        /* transient poll error — keep polling */
      }
    }, 3000);
    return () => clearInterval(t);
  }, [docs]);

  async function onFileChosen(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,   // give the AI 90 seconds for large PDFs
      });
      if (data.ai_status === "failed") {
        setError(data.plain_summary || "AI processing failed. The original file was saved.");
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed. Try a smaller file.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDelete(docId) {
    if (!window.confirm("Delete this document? The original file will be removed permanently.")) return;
    try {
      await api.delete(`/documents/${docId}`);
      setSelectedDoc(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header + upload */}
      <div className="ros-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl">Medical documents</h2>
            <p className="text-sm text-[#A3ACA7] mt-1">
              Upload any lab, prescription, radiology, discharge, pathology, or operative report.
              Med Nexus reads it, explains it in plain English, and keeps the original safe.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,application/pdf,image/*"
              onChange={onFileChosen}
              className="hidden"
              data-testid="documents-file-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="documents-upload-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4ADE80] text-black font-semibold hover:bg-[#22C55E] disabled:opacity-50 transition-colors"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? "Uploading…" : "Upload document"}
            </button>
          </div>
        </div>
        {error && (
          <div className="mt-4 text-sm text-[#F87171] bg-[#F87171]/10 border border-[#F87171]/20 rounded-lg p-3 flex items-start gap-2"
               data-testid="documents-error">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-[#A3ACA7] py-16">Loading your documents…</div>
      ) : docs.length === 0 ? (
        <div className="ros-card text-center py-16" data-testid="documents-empty">
          <FileText size={40} className="mx-auto text-[#A3ACA7] mb-4" />
          <div className="font-heading text-xl">No documents yet</div>
          <div className="text-sm text-[#A3ACA7] mt-2 max-w-md mx-auto">
            Upload your first lab, prescription, or medical report and Med Nexus will explain what it means.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="documents-grid">
          {docs.map((d) => {
            const meta = DOC_TYPE_META[d.doc_type] || DOC_TYPE_META.other;
            const Icon = meta.icon;
            const isFailed = d.ai_status === "failed";
            return (
              <button
                key={d.id}
                onClick={() => setSelectedDoc(d)}
                data-testid={`document-card-${d.id}`}
                className="text-left ros-card hover:border-[#4ADE80]/40 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${meta.accent}18`, border: `1px solid ${meta.accent}33` }}
                  >
                    <Icon size={18} style={{ color: meta.accent }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white text-sm">{meta.label}</span>
                      {isFailed && (
                        <span className="text-[10px] font-bold text-[#F87171] px-2 py-0.5 rounded-full bg-[#F87171]/10">
                          AI failed
                        </span>
                      )}
                      {(d.ai_status === "pending" || d.ai_status === "processing") && (
                        <span className="text-[10px] font-bold text-[#FBBF24] px-2 py-0.5 rounded-full bg-[#FBBF24]/10 inline-flex items-center gap-1">
                          <Clock size={10} className="animate-pulse" />
                          {d.ai_status === "pending" ? "Queued" : "Analyzing…"}
                        </span>
                      )}
                      {d.ai_status === "completed" && (
                        <span className="text-[10px] font-bold text-[#4ADE80] px-2 py-0.5 rounded-full bg-[#4ADE80]/10 inline-flex items-center gap-1">
                          <CheckCircle2 size={10} /> Analyzed
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#A3ACA7] mt-1 truncate">{d.original_filename}</div>
                    <div className="text-[11px] text-[#7A8480] mt-1">
                      {d.doc_date ? `Report ${formatDate(d.doc_date)}` : `Uploaded ${formatDate(d.created_at)}`}
                      {d.facility_name && <> · {d.facility_name}</>}
                    </div>
                    {d.plain_summary ? (
                      <div className="text-sm text-[#D1D5DB] mt-3 line-clamp-2">
                        {d.plain_summary}
                      </div>
                    ) : (d.ai_status === "pending" || d.ai_status === "processing") ? (
                      <div className="text-sm text-[#A3ACA7] mt-3 italic">
                        Med Nexus is reading your document — this usually takes 10–15 seconds.
                      </div>
                    ) : null}
                  </div>
                  <ChevronRight size={16} className="text-[#A3ACA7] group-hover:text-[#4ADE80] transition-colors" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectedDoc && (
        <DocumentDetailModal
          doc={selectedDoc}
          onClose={() => setSelectedDoc(null)}
          onDelete={() => handleDelete(selectedDoc.id)}
        />
      )}
    </div>
  );
}

function DocumentDetailModal({ doc, onClose, onDelete }) {
  const meta = DOC_TYPE_META[doc.doc_type] || DOC_TYPE_META.other;
  const Icon = meta.icon;
  const s = doc.structured_data || {};

  const download = async () => {
    try {
      const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert("Download failed");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="document-detail-modal"
    >
      <div
        className="bg-[#0F1312] border border-[#272E2B] rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[#272E2B] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: `${meta.accent}18`, border: `1px solid ${meta.accent}33` }}
            >
              <Icon size={16} style={{ color: meta.accent }} />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">{meta.label}</div>
              <div className="text-[11px] text-[#A3ACA7]">{doc.original_filename}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-[#A3ACA7] hover:text-white" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto space-y-5">
          {doc.plain_summary && (
            <div>
              <div className="ros-label mb-2">Plain-English summary</div>
              <div className="text-sm text-white leading-relaxed">{doc.plain_summary}</div>
            </div>
          )}

          {Array.isArray(doc.highlights) && doc.highlights.length > 0 && (
            <div>
              <div className="ros-label mb-2">Key findings</div>
              <ul className="space-y-1.5">
                {doc.highlights.map((h, i) => (
                  <li key={i} className="text-sm text-white flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] mt-2 shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Doc-type-specific extracted data */}
          {doc.doc_type === "lab_report" && Array.isArray(s.markers) && (
            <div>
              <div className="ros-label mb-2">Lab values</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] text-[#A3ACA7] uppercase tracking-wider">
                      <th className="py-2 pr-4">Marker</th>
                      <th className="py-2 pr-4">Value</th>
                      <th className="py-2 pr-4">Range</th>
                      <th className="py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.markers.map((m, i) => {
                      const flag = (m.flag || "").toLowerCase();
                      const color = flag === "high" ? "#F87171" : flag === "low" ? "#FBBF24" : "#4ADE80";
                      return (
                        <tr key={i} className="border-t border-[#272E2B]">
                          <td className="py-2 pr-4 text-white">{m.name}</td>
                          <td className="py-2 pr-4 text-white font-mono">{m.value} {m.unit || ""}</td>
                          <td className="py-2 pr-4 text-[#A3ACA7]">{m.reference_range || "—"}</td>
                          <td className="py-2" style={{ color }}>{(m.flag || "normal").toUpperCase()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {doc.doc_type === "prescription" && Array.isArray(s.medications) && (
            <div>
              <div className="ros-label mb-2">Medications</div>
              <ul className="space-y-2">
                {s.medications.map((m, i) => (
                  <li key={i} className="ros-card-raised">
                    <div className="font-semibold text-white text-sm">{m.name}</div>
                    <div className="text-xs text-[#A3ACA7] mt-1">
                      {m.dose} · {m.frequency}{m.duration ? ` · ${m.duration}` : ""}
                    </div>
                    {m.instructions && (
                      <div className="text-xs text-[#D1D5DB] mt-1">{m.instructions}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(doc.doc_type === "radiology_report" || doc.doc_type === "pathology_report") && s.impression && (
            <div>
              <div className="ros-label mb-2">Impression</div>
              <div className="text-sm text-white whitespace-pre-wrap">{s.impression || s.diagnosis}</div>
            </div>
          )}

          {doc.doc_type === "discharge_summary" && (
            <div className="space-y-2 text-sm">
              {s.primary_diagnosis && (
                <div><span className="text-[#A3ACA7]">Primary diagnosis:</span> <span className="text-white">{s.primary_diagnosis}</span></div>
              )}
              {s.follow_up_instructions && (
                <div><span className="text-[#A3ACA7]">Follow-up:</span> <span className="text-white">{s.follow_up_instructions}</span></div>
              )}
            </div>
          )}

          <div className="pt-4 border-t border-[#272E2B] flex items-center justify-between text-xs text-[#7A8480]">
            <div>
              {doc.facility_name && <>{doc.facility_name} · </>}
              {doc.doc_date && <>Report {formatDate(doc.doc_date)} · </>}
              Uploaded {formatDate(doc.created_at)}
            </div>
          </div>
        </div>
        <div className="px-6 py-3 border-t border-[#272E2B] flex items-center justify-between">
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 text-sm text-[#F87171] hover:text-white transition-colors"
            data-testid="document-delete-btn"
          >
            <Trash2 size={14} /> Delete
          </button>
          <button
            onClick={download}
            className="flex items-center gap-1.5 text-sm text-white bg-[#1C201E] hover:bg-[#272E2B] border border-[#272E2B] rounded-lg px-4 py-2 transition-colors"
            data-testid="document-download-btn"
          >
            <Download size={14} /> Download original
          </button>
        </div>
      </div>
    </div>
  );
}
